import { isDefined } from "@clipboard-health/util-ts";
import { API_TYPES } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { type z } from "zod";

import {
  type PlacementCandidate,
  type PlacementCandidateIncludedBan,
  type PlacementCandidateIncludedRequirement,
  type PlacementCandidateIncludedWorkerRequirement,
  type PlacementCandidateWorkerAttributeIncluded,
} from "../types";
import { type createPlacementCandidateResponseSchema } from "./useCreateOrGetPlacementCandidate";
import { type placementCandidateResponseSchema } from "./useGetPlacementCandidate";

export const placementCandidateApiUrl = `${environmentConfig.REACT_APP_BASE_API_URL}/placement-candidates`;

type TransformResponseIntoPlacementCandidateResponse =
  | z.infer<typeof placementCandidateResponseSchema>
  | z.infer<typeof createPlacementCandidateResponseSchema>;

export function transformPlacementCandidateWorkerRequirements(
  placementCandidate: z.infer<typeof placementCandidateResponseSchema>["data"][number],
  response: TransformResponseIntoPlacementCandidateResponse
) {
  return placementCandidate.relationships.workerRequirements?.data.map((workerRequirement) => {
    const workerRequirementData = response.included.find(
      (included): included is PlacementCandidateIncludedWorkerRequirement =>
        included.type === API_TYPES.workerRequirement && included.id === workerRequirement.id
    );
    if (!workerRequirementData) {
      return undefined;
    }

    const requirementData = response.included.find(
      (included): included is PlacementCandidateIncludedRequirement =>
        included.type === API_TYPES.requirement &&
        included.id === workerRequirementData?.relationships.requirement.data.id
    );
    if (!requirementData) {
      return undefined;
    }

    return {
      id: workerRequirementData.id,
      ...workerRequirementData.attributes,
      requirement: {
        id: requirementData.id,
        ...requirementData.attributes,
      },
    };
  });
}

function buildWorkerObject(worker: PlacementCandidateWorkerAttributeIncluded) {
  const {
    cbhPassportDocuments,
    completedShiftsCount,
    distance,
    email,
    name,
    phone,
    profileImageUrl,
    qualification,
  } = worker.attributes;
  const { id: workerId } = worker;

  return {
    id: workerId,
    name,
    qualification,
    ...(distance !== undefined && { distance }),
    ...(completedShiftsCount !== undefined && { completedShiftsCount }),
    ...(cbhPassportDocuments !== undefined && { cbhPassportDocuments }),
    ...(email !== undefined && { email }),
    ...(phone !== undefined && { phone }),
    ...(profileImageUrl !== undefined && { profileImageUrl }),
  };
}

function buildCandidateAttributes(
  placementCandidate: z.infer<typeof placementCandidateResponseSchema>["data"][number]
) {
  const {
    preferredAreas,
    preferredPayRate,
    currentYearsOfExperience,
    yearsOfExperienceByQualification,
    preferredDaysOfWeek,
    payAndScheduleFormData,
  } = placementCandidate.attributes;

  return {
    ...(isDefined(preferredAreas) && { preferredAreas }),
    ...(isDefined(preferredPayRate) && { preferredPayRate }),
    ...(isDefined(currentYearsOfExperience) && { currentYearsOfExperience }),
    ...(isDefined(yearsOfExperienceByQualification) && { yearsOfExperienceByQualification }),
    ...(isDefined(preferredDaysOfWeek) && { preferredDaysOfWeek }),
    ...(isDefined(payAndScheduleFormData) && { payAndScheduleFormData }),
  };
}

export function transformResponseIntoPlacementCandidate(
  response: TransformResponseIntoPlacementCandidateResponse
): PlacementCandidate {
  const placementCandidate = Array.isArray(response.data) ? response.data[0] : response.data;

  const worker = response.included.find(
    (item): item is PlacementCandidateWorkerAttributeIncluded =>
      item.type === "workers" && item.id === placementCandidate.relationships.worker.data.id
  );

  if (!worker) {
    throw new Error("Candidate details not found");
  }

  const mostRecentBan = response.included.find(
    (item): item is PlacementCandidateIncludedBan =>
      item.type === API_TYPES.placementCandidateBan &&
      item.id === placementCandidate.relationships.mostRecentBan?.data.id
  );

  const workerRequirements = transformPlacementCandidateWorkerRequirements(
    placementCandidate,
    response
  );

  const { attributes, id: placementCandidateId, meta } = placementCandidate;
  const {
    status,
    introductionUrl,
    screeningStatus,
    passportUrl,
    initialOnboardingFlowCompleted,
    attendanceScore,
  } = attributes;
  const { bansCountByAction } = meta ?? {};

  return {
    id: placementCandidateId,
    status,
    screeningStatus,
    introductionUrl,
    passportUrl,
    attendanceScore,
    ...buildCandidateAttributes(placementCandidate),
    worker: buildWorkerObject(worker),
    workerRequirements: workerRequirements?.filter(isDefined),
    mostRecentBan: mostRecentBan
      ? { ...mostRecentBan.attributes, id: mostRecentBan.id }
      : undefined,
    isRestricted: mostRecentBan && !mostRecentBan.attributes.revokedAt,
    initialOnboardingFlowCompleted: initialOnboardingFlowCompleted ?? false,
    ...(isDefined(bansCountByAction) && { bansCountByAction }),
  };
}

export function getPlacementCandidateQueryKey(workerId: string) {
  return [placementCandidateApiUrl, workerId];
}
